import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { AdvertiseLayout } from '@/components/advertise/AdvertiseLayout';
import { AdvertiseSideSummary } from '@/components/advertise/AdvertiseSideSummary';
import { AdvertiseFooterNav } from '@/components/advertise/AdvertiseFooterNav';
import { useAdvertise } from '@/store/useAdvertise';
import { useStepGuard, agentsStepValid } from '@/utils/advertiseGuards';
import { AGENTS_STEPS } from '@/config/advertiseSteps';

const TIMELINES = [
  { id: 'immediate', label: 'Immediately' },
  { id: 'next-30-days', label: 'Next 30 days' },
  { id: 'just-exploring', label: 'Just exploring' },
];

const COMPETITORS = ['Zillow', 'Realtor.com', 'Redfin', 'Other', 'None currently'];

export default function AgentsGoals() {
  const navigate = useNavigate();
  const agents = useAdvertise((s) => s.agents);
  const setField = useAdvertise((s) => s.setField);
  useStepGuard(agentsStepValid.goals(agents), '/advertise/agents');
  const [marketInput, setMarketInput] = useState('');

  const set = (patch) => setField('agents', patch);

  const addMarket = () => {
    const v = marketInput.trim();
    if (v && !agents.markets.includes(v)) set({ markets: [...agents.markets, v] });
    setMarketInput('');
  };
  const removeMarket = (m) => set({ markets: agents.markets.filter((x) => x !== m) });

  const toggleCompetitor = (c) => {
    const has = agents.competitors.includes(c);
    set({ competitors: has ? agents.competitors.filter((x) => x !== c) : [...agents.competitors, c] });
  };

  const canProceed = !!agents.activeListings && !!agents.timeline;

  return (
    <AdvertiseLayout
      trackLabel="Agent advertising"
      steps={AGENTS_STEPS}
      currentStepId="goals"
      title="A few details about your business"
      subtitle="This helps us tailor a recommendation before your call."
      sidebar={
        <AdvertiseSideSummary
          items={[
            { label: 'Active listings', value: agents.activeListings },
            { label: 'Timeline', value: TIMELINES.find((t) => t.id === agents.timeline)?.label },
            { label: 'Markets', value: agents.markets.join(', ') },
          ]}
        />
      }
      footer={<AdvertiseFooterNav nextDisabled={!canProceed} onBack={() => navigate('/advertise/agents/profile')} onNext={() => navigate('/advertise/agents/schedule')} />}
    >
      <div className="space-y-6">
        <div>
          <Label>How many active listings do you have?</Label>
          <Select value={agents.activeListings} onValueChange={(v) => set({ activeListings: v })}>
            <SelectTrigger className="mt-1.5 rounded-xl" data-testid="agents-goals-active-listings"><SelectValue placeholder="Select a range" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="0-5">0–5</SelectItem>
              <SelectItem value="6-25">6–25</SelectItem>
              <SelectItem value="25+">25+</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Primary markets you serve</Label>
          <div className="mt-1.5 flex gap-2">
            <Input value={marketInput} onChange={(e) => setMarketInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addMarket(); } }} placeholder="e.g. Boca Raton, FL" className="rounded-xl" data-testid="agents-goals-market-input" />
          </div>
          {agents.markets.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {agents.markets.map((m) => (
                <span key={m} className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">
                  {m}<button type="button" onClick={() => removeMarket(m)} aria-label={`Remove ${m}`}><X size={12} /></button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div>
          <Label>When are you looking to get started?</Label>
          <RadioGroup value={agents.timeline} onValueChange={(v) => set({ timeline: v })} className="mt-1.5 grid gap-2 sm:grid-cols-3">
            {TIMELINES.map((t) => (
              <Label key={t.id} htmlFor={`timeline-${t.id}`} className="flex cursor-pointer items-center gap-2 rounded-xl border border-slate-200 px-3 py-2.5 has-[[data-state=checked]]:border-blue-600 has-[[data-state=checked]]:bg-blue-50">
                <RadioGroupItem value={t.id} id={`timeline-${t.id}`} data-testid={`agents-goals-timeline-${t.id}`} />
                <span className="text-sm font-medium text-slate-800">{t.label}</span>
              </Label>
            ))}
          </RadioGroup>
        </div>

        <div>
          <Label>Are you currently advertising elsewhere?</Label>
          <div className="mt-1.5 grid gap-2 sm:grid-cols-2">
            {COMPETITORS.map((c) => (
              <label key={c} className="flex cursor-pointer items-center gap-2 rounded-xl border border-slate-200 px-3 py-2.5">
                <Checkbox checked={agents.competitors.includes(c)} onCheckedChange={() => toggleCompetitor(c)} data-testid={`agents-goals-competitor-${c.toLowerCase().replace(/[^a-z]+/g, '-')}`} />
                <span className="text-sm text-slate-700">{c}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
    </AdvertiseLayout>
  );
}
